import { Card, Group, Text, Stack, ActionIcon, Tooltip, CopyButton, rem, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { UserService } from '../../types';
import { useEffect, useState } from 'react';

export const VirtualAddress = () => {
    const [id, setId] = useState<string | null>(null);
    const [username, setUsername] = useState<string | null>(null);
    const isMobile = useMediaQuery('(max-width: 768px)');

    // Company info and contact details
    const companyName = "OTL Courier(pvt) Ltd";
    const phoneNumbers = ["0112555183", "+94 77 123 4567"];

    // Complete address parts following the FORWARD2ME layout format
    const addressParts = [
        { label: "Name", value: username },
        { label: "Address line 1", value: "No 13 Lauries Road" },
        { label: "Address line 2", value: id ? `User ID: ${id}` : '[Your User ID]' },
        { label: "City", value: "Colombo 04" },
        { label: "Province", value: "Western Province" },
        { label: "Postal Code", value: "00400" },
        { label: "Company", value: companyName },
        { label: "Country", value: "Sri Lanka" },
        { label: "Phone", value: phoneNumbers[0] }
    ];

    // Complete address for copy all
    const getCompleteAddress = () => {
        return `${companyName}\nNo 13 Lauries road\nUser ID: ${id || '[Your User ID]'}\nColombo 04\nWestern Province\n00400\nSri Lanka\n${phoneNumbers[0]}`;
    };

    // fetch user data
    const fetchUserData = async () => {
        try {
            const userInfoString = sessionStorage.getItem('userInfo');
            if (userInfoString) {
                const decodedJson = JSON.parse(atob(userInfoString));

                const response = await UserService.getUserById(decodedJson.sub ? decodedJson.sub : '');
                setId(response.userId || null);
                setUsername(
                    response.first_name && response.last_name
                        ? `${response.first_name} ${response.last_name}`
                        : response.first_name || response.last_name || null
                );
            }
        } catch (error) {
        }
    }

    // Fetch user data when the component mounts
    useEffect(() => {
        if (!id || !username) {
            fetchUserData();
        }
    }, [id, username]);

    return (
        <Card withBorder shadow="sm" radius="md" p={isMobile ? "xs" : "lg"} h="100%">
            <Group justify="space-between" align="center" mb="md" wrap="nowrap">
                <Title order={3} size={isMobile ? "h5" : "h4"}>Virtual Address</Title>

                <CopyButton value={getCompleteAddress()} timeout={3000}>
                    {({ copied, copy }) => (
                        <ActionIcon
                            onClick={copy}
                            variant={copied ? "filled" : "light"}
                            color={copied ? "teal" : "blue"}
                            size={isMobile ? "md" : "lg"}
                        >
                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                        </ActionIcon>
                    )}
                </CopyButton>
            </Group>
            <Stack gap={isMobile ? "xs" : "md"}>
                {/* Copy All Address Button */}
                <Group justify="space-between" mb={isMobile ? "xs" : "md"}>
                    <Text size="sm" fw={600} c="blue.6">
                        Use this virtual address for all your online purchases. Include your User ID in Address 2 to ensure proper package identification and delivery.
                    </Text>
                </Group>
                <Stack gap={0}>
                    {addressParts.map((part, index) => {
                        const key = `address-part-${index}`;
                        return isMobile ? (
                            <Stack
                                key={key}
                                gap="xs"
                                style={{
                                    padding: '8px 0',
                                    borderBottom: index < addressParts.length - 1 ? '1px solid #e0e0e0' : 'none',
                                    width: '100%'
                                }}
                            >
                                <Text
                                    size="sm"
                                    fw={500}
                                    c="gray.6"
                                    style={{ width: '100%' }}
                                >
                                    {part.label} :
                                </Text>
                                <Group gap="sm" align="center" style={{ width: '100%' }} justify="space-between">
                                    <Text
                                        size="sm"
                                        fw={600}
                                        c="blue.9"
                                        style={{
                                            textAlign: 'left',
                                            fontFamily: part.label,
                                            wordBreak: 'break-word',
                                            flex: 1
                                        }}
                                    >
                                        {part.value || (part.label === "Address 2" ? "Loading..." : "")}
                                    </Text>
                                    <CopyButton value={part.value || ''} timeout={2000}>
                                        {({ copied, copy }) => (
                                            <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="top">
                                                <ActionIcon
                                                    color={copied ? 'teal' : part.label === 'gray' ? 'gray' : 'dark'}
                                                    onClick={copy}
                                                    variant="subtle"
                                                    size="sm"
                                                >
                                                    {copied ? (
                                                        <IconCheck style={{ width: rem(14) }} />
                                                    ) : (
                                                        <IconCopy style={{ width: rem(14) }} />
                                                    )}
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </CopyButton>
                                </Group>
                            </Stack>
                        ) : (
                            <Group
                                key={key}
                                justify="center"
                                align="center"
                                wrap="nowrap"
                                style={{
                                    padding: '12px 0',
                                    borderBottom: index < addressParts.length - 1 ? '1px solid #e0e0e0' : 'none'
                                }}
                            >
                                <Text
                                    size="sm"
                                    fw={500}
                                    c="gray.6"
                                    style={{ width: '120px', textAlign: 'right' }}
                                >
                                    {part.label} :
                                </Text>
                                <Group gap="sm" align="center">
                                    <Text
                                        size="sm"
                                        fw={600}
                                        c="blue.9"
                                        style={{
                                            textAlign: 'left',
                                            fontFamily: part.label,
                                            minWidth: '200px'
                                        }}
                                    >
                                        {part.value || (part.label === "Address 2" ? "Loading..." : "")}
                                    </Text>
                                    <CopyButton value={part.value || ''} timeout={2000}>
                                        {({ copied, copy }) => (
                                            <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="right">
                                                <ActionIcon
                                                    color={copied ? 'teal' : part.label === 'gray' ? 'gray' : 'dark'}
                                                    onClick={copy}
                                                    variant="subtle"
                                                    size="sm"
                                                >
                                                    {copied ? (
                                                        <IconCheck style={{ width: rem(14) }} />
                                                    ) : (
                                                        <IconCopy style={{ width: rem(14) }} />
                                                    )}
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </CopyButton>
                                </Group>
                            </Group>
                        );
                    })}
                </Stack>
            </Stack>
        </Card>
    );
};
